import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const ProfessionalDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickActions, setQuickActions] = useState([]);

  useEffect(() => {
    fetchAnalytics();
    setupQuickActions();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const setupQuickActions = () => {
    setQuickActions([
      {
        title: 'Add New Adventure',
        description: 'Create a new diving adventure',
        icon: '🤿',
        action: () => window.location.href = '/admin/adventures?action=create',
        color: 'bg-blue-500'
      },
      {
        title: 'Manage Calendar',
        description: 'Set availability and pricing',
        icon: '📅',
        action: () => window.location.href = '/admin/calendar',
        color: 'bg-green-500'
      },
      {
        title: 'Upload Media',
        description: 'Add photos and videos',
        icon: '📸',
        action: () => window.location.href = '/admin/gallery',
        color: 'bg-purple-500'
      },
      {
        title: 'Edit Website',
        description: 'Update content and design',
        icon: '✏️',
        action: () => window.location.href = '/admin/reviews',
        color: 'bg-orange-500'
      },
      {
        title: 'View Bookings',
        description: 'Manage customer bookings',
        icon: '📋',
        action: () => window.location.href = '/admin/bookings',
        color: 'bg-red-500'
      },
      {
        title: 'Site Settings',
        description: 'Configure global settings',
        icon: '⚙️',
        action: () => window.location.href = '/admin/settings',
        color: 'bg-gray-500'
      }
    ]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg p-8 text-white"
      >
        <h1 className="text-3xl font-bold mb-2">Welcome to Professional CMS</h1>
        <p className="text-blue-100">Complete control over your diving business website</p>
        <div className="mt-4 flex items-center space-x-6 text-sm">
          <div className="flex items-center">
            <span className="text-blue-200">Last updated:</span>
            <span className="ml-2 font-medium">{new Date().toLocaleDateString()}</span>
          </div>
          <div className="flex items-center">
            <span className="text-blue-200">System Status:</span>
            <span className="ml-2 px-2 py-1 bg-green-500 rounded text-xs font-medium">Online</span>
          </div>
        </div>
      </motion.div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Bookings</p>
                <p className="text-3xl font-bold text-gray-900">{analytics.total_bookings}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <span className="text-2xl">📅</span>
              </div>
            </div>
            <div className="mt-4">
              <span className="text-sm text-gray-500">Last 30 days</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Revenue</p>
                <p className="text-3xl font-bold text-gray-900">${analytics.total_revenue?.toLocaleString() || '0'}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <span className="text-2xl">💰</span>
              </div>
            </div>
            <div className="mt-4">
              <span className="text-sm text-gray-500">AUD Total</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Adventures</p>
                <p className="text-3xl font-bold text-gray-900">{analytics.total_adventures}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <span className="text-2xl">🤿</span>
              </div>
            </div>
            <div className="mt-4">
              <span className="text-sm text-gray-500">Active Adventures</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Media Assets</p>
                <p className="text-3xl font-bold text-gray-900">{analytics.total_media_assets}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <span className="text-2xl">📸</span>
              </div>
            </div>
            <div className="mt-4">
              <span className="text-sm text-gray-500">Images & Videos</span>
            </div>
          </motion.div>
        </div>
      )}

      {/* Quick Actions Grid */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {quickActions.map((action, index) => (
            <motion.div
              key={action.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
              onClick={action.action}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer group"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                    {action.title}
                  </h3>
                  <p className="text-gray-600 mt-1">{action.description}</p>
                </div>
                <div className={`p-3 ${action.color} rounded-lg ml-4`}>
                  <span className="text-2xl">{action.icon}</span>
                </div>
              </div>
              <div className="mt-4">
                <span className="text-sm text-blue-600 font-medium group-hover:underline">
                  Get Started →
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Popular Adventures */}
      {analytics?.popular_adventures && analytics.popular_adventures.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Popular Adventures</h2>
          <div className="space-y-4">
            {analytics.popular_adventures.map((adventure, index) => (
              <div key={adventure._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-blue-600">#{index + 1}</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{adventure.title}</h3>
                    <p className="text-sm text-gray-600">
                      {adventure.booking_count} bookings • {adventure.view_count} views
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => window.location.href = `/admin/adventures?edit=${adventure._id}`}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Manage →
                </button>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* System Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
      >
        <h2 className="text-xl font-semibold text-gray-900 mb-4">System Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl">✅</span>
            </div>
            <h3 className="font-medium text-green-900">Website Status</h3>
            <p className="text-sm text-green-700 mt-1">Online & Responsive</p>
          </div>
          
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl">🔒</span>
            </div>
            <h3 className="font-medium text-blue-900">Security</h3>
            <p className="text-sm text-blue-700 mt-1">SSL Active</p>
          </div>
          
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl">⚡</span>
            </div>
            <h3 className="font-medium text-purple-900">Performance</h3>
            <p className="text-sm text-purple-700 mt-1">Optimized</p>
          </div>
        </div>
      </motion.div>

      {/* Tips & Help */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-6 border border-indigo-200"
      >
        <h2 className="text-xl font-semibold text-gray-900 mb-4">💡 Professional Tips</h2>
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <span className="text-lg">🎯</span>
            <p className="text-gray-700">
              <strong>Pro Tip:</strong> Update your adventure availability weekly for better booking rates.
            </p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="text-lg">📸</span>
            <p className="text-gray-700">
              <strong>Media Tip:</strong> High-quality images increase bookings by up to 40%.
            </p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="text-lg">⭐</span>
            <p className="text-gray-700">
              <strong>Customer Tip:</strong> Respond to inquiries within 2 hours for best conversion.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ProfessionalDashboard;